import React, {
  useCallback,
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { getGridStringOperators } from '@mui/x-data-grid';
import { Delete } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import PodContext from '../../../../../context/PodContext/PodContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import InternalAssignment from '../../../../../Model/InternalAssignment';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import Table from '../../../../components/Table';
import useQuickSearch from '../../../../../hooks/useQuickSearch';
import CoachPod from '../../../../../Model/CoachPod';
import useToast from '../../../../hooks/useToast';
import CoachHistory, { HistoryEventType } from '../../../../Model/CoachHistory';

import { ActionButtonContainer, DialogContentContainer } from './styles';

import texts from './texts.json';

const CoachPodTable = ({ selectedCoach }) => {
  const { pods, fetchPodsForCoach, assistantCoaches } = useContext(PodContext);
  const {
    userDoc: {
      name: authUserName,
    },
    userId: authUserId,
  } = useContext(LoggedInUserContext);

  const [podsWithAssignments, setPodsWithAssignments] = useState([]);
  const [isPodDeleting, setIsPodDeleting] = useState(false);
  const [isPodUpdating, setIsPodUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateACDialogOpen, setUpdateACDialogOpen] = useState(false);
  const [selectedPodId, setSelectedPodId] = useState(null);
  const [selectedACs, setSelectedACs] = useState([]);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const init = async () => {
      const assistantAssignments = await InternalAssignment.getAllCurrentAssignments(null, 'AC', selectedCoach.id);

      if (!isComponentMountedRef.current) return;

      const assistantMap = assistantAssignments.docs.reduce((map, assignment) => {
        const { podId, userId, userName } = assignment;

        if (podId) {
          if (!map.has(podId)) {
            map.set(podId, []);
          }
          map.get(podId).push({ userId, userName });
        }

        return map;
      }, new Map());

      const newPods = pods.map((pod) => {
        const { id } = pod;
        const { name, userCount, coach } = pod.data;
        const assignments = assistantMap.get(id) || [];

        return {
          id,
          name,
          userCount,
          coach,
          assistantCoaches: assignments,
        };
      });

      if (isComponentMountedRef.current) {
        setPodsWithAssignments(newPods);
      }
    };

    init();
  }, [isComponentMountedRef, pods, selectedCoach.id]);

  const addACtoPod = useCallback(async (acId) => {
    try {
      setIsPodUpdating(true);
      const assignments = await InternalAssignment.getAllCurrentAssignments(acId, 'AC', selectedCoach.id);
      await Promise.all(assignments.docs?.map((assignment) => assignment.updateFields({ podId: selectedPodId })));

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_ASSISTANT_ADDED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
        metadata: {
          podId: selectedPodId,
          assistantCoach: acId,
        },
      });
    } catch (error) {
      showToast(texts.assignmentUpdateError, { error: true });
    } finally {
      setIsPodUpdating(false);
    }
  }, [authUserId, authUserName, selectedCoach.id, selectedPodId, showToast]);

  const removeACFromPod = useCallback(async (acId) => {
    try {
      setIsPodUpdating(true);
      const assignment = await InternalAssignment.getAssignmentsByPodIdAnsAC(selectedPodId, acId);
      assignment?.updateFields({ podId: null });

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_ASSISTANT_REMOVED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
        metadata: {
          podId: selectedPodId,
          assistantCoach: acId,
        },
      });
    } catch (error) {
      showToast(texts.assignmentUpdateError, { error: true });
    } finally {
      setIsPodUpdating(false);
    }
  }, [authUserId, authUserName, selectedCoach.id, selectedPodId, showToast]);

  const handleACSelect = useCallback(async (event) => {
    const {
      target: { value },
    } = event;
    const addedAC = value.find((item) => !selectedACs.includes(item));
    const removedAC = selectedACs.find((item) => !value.includes(item));

    if (addedAC) {
      await addACtoPod(addedAC);
    }

    if (removedAC) {
      await removeACFromPod(removedAC);
    }
    setSelectedACs(value);
  }, [addACtoPod, removeACFromPod, selectedACs]);

  const handleDeletePodModalOpen = useCallback((podId) => {
    setDeleteDialogOpen(true);
    setSelectedPodId(podId);
  }, [setSelectedPodId]);

  const handleDeletePodModalClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleUpdateACModalOpen = useCallback((podId) => {
    const selectedPod = podsWithAssignments.find((pod) => pod.id === podId);

    const currentACIds = selectedPod?.assistantCoaches?.map((ac) => ac.userId) || [];

    setSelectedACs(currentACIds);
    setUpdateACDialogOpen(true);
    setSelectedPodId(podId);
  }, [podsWithAssignments]);

  const handleUpdateACModalClose = async () => {
    await fetchPodsForCoach(selectedCoach.id);
    setSelectedACs([]);
    setUpdateACDialogOpen(false);
  };

  const applyStringOperator = (text, searchValue, operator) => {
    switch (operator) {
      case 'contains':
        return text.includes(searchValue);
      case 'equals':
        return text === searchValue;
      case 'startsWith':
        return text.startsWith(searchValue);
      case 'endsWith':
        return text.endsWith(searchValue);
      case 'isAnyOf':
        return Array.isArray(searchValue) ? searchValue.includes(text) : false;
      default:
        return text.includes(searchValue);
    }
  };

  const getAcsDisplayValue = (acs) => (acs.length === 0
    ? texts.notAssigned
    : acs.map((ac) => ac.userName).join(', '));

  const createACsFilterFn = useCallback((filterItem) => {
    if (!filterItem.field) return null;

    return (params) => {
      const acs = params.row.assistantCoaches;
      const { value: searchValue, operator: operatorValue } = filterItem;

      if (operatorValue === 'isEmpty') return acs.length === 0;
      if (operatorValue === 'isNotEmpty') return acs.length > 0;
      if (!searchValue) return true;

      if (acs.length === 0) {
        const notAssignedText = texts.notAssigned.toLowerCase();
        const searchLower = typeof searchValue === 'string'
          ? searchValue.toLowerCase()
          : searchValue;
        return applyStringOperator(notAssignedText, searchLower, operatorValue);
      }

      return acs.some((coach) => {
        const coachName = coach.userName.toLowerCase();
        const searchLower = typeof searchValue === 'string'
          ? searchValue.toLowerCase()
          : searchValue;
        return applyStringOperator(coachName, searchLower, operatorValue);
      });
    };
  }, []);

  const handlePodDelete = useCallback(async () => {
    try {
      setIsPodDeleting(true);
      await CoachPod.delete(selectedPodId);
      const assignmentsForPod = await InternalAssignment.getAssignmentsByPodId(selectedPodId);
      if (assignmentsForPod) {
        await Promise.all(assignmentsForPod.map((assignment) => assignment.updateFields({ podId: null })));
      }

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_DELETED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
        metadata: {
          podId: selectedPodId,
        },
      });

      setDeleteDialogOpen(false);
      showToast(texts.podDeleted, { success: true });

      await fetchPodsForCoach(selectedCoach.id);
    } catch (error) {
      showToast(texts.podDeletionError, { error: true });
    } finally {
      setIsPodDeleting(false);
      setSelectedPodId(null);
    }
  }, [selectedPodId, selectedCoach.id, authUserId, authUserName, showToast, fetchPodsForCoach]);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        quickSearch: true,
        headerName: texts.podName,
        flex: 30,
      },
      {
        field: 'userCount',
        quickSearch: true,
        headerName: texts.userCount,
        flex: 30,
      },
      {
        field: 'assistantCoaches',
        headerName: texts.assistantCoaches,
        flex: 30,
        valueGetter: (params) => getAcsDisplayValue(params.row.assistantCoaches),
        filterOperators: getGridStringOperators().map((operator) => ({
          ...operator,
          getApplyFilterFn: createACsFilterFn,
        })),
        renderCell: ({ row }) => getAcsDisplayValue(row.assistantCoaches),
      },
      {
        field: 'action',
        quickSearch: true,
        headerName: texts.action,
        flex: 10,
        renderCell: ({ row }) => (
          <ActionButtonContainer>
            <Button onClick={() => handleUpdateACModalOpen(row.id)} variant="contained" size="small">
              {row.assistantCoaches.length === 0 ? texts.assign : texts.update}
            </Button>
            {row.userCount === 0 && (
              <IconButton onClick={() => handleDeletePodModalOpen(row.id)} disabled={isPodDeleting}>
                <Delete />
              </IconButton>
            )}
          </ActionButtonContainer>
        ),
      },
    ],
    [createACsFilterFn, handleDeletePodModalOpen, handleUpdateACModalOpen, isPodDeleting],
  );

  const { filteredRows } = useQuickSearch(podsWithAssignments, columns);

  const getSelectedNames = useCallback((ids) => assistantCoaches
    .filter((coach) => ids.includes(coach.id))
    .map((coach) => coach.firstName)
    .join(', '), [assistantCoaches]);

  return (
    <>
      <Table columns={columns} rows={filteredRows} />

      <Dialog
        open={updateACDialogOpen}
        onClose={handleUpdateACModalClose}
      >
        <DialogTitle id="update-ac">
          {texts.updateACTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentContainer>
            <DialogContentText id="alert-dialog-description">
              {texts.updatePodConfirmation}
            </DialogContentText>
            <FormControl fullWidth>
              <InputLabel id="ac-select-label">{texts.assistantCoaches}</InputLabel>
              <Select
                labelId="ac-select-label"
                value={selectedACs}
                label={texts.assistantCoaches}
                onChange={handleACSelect}
                multiple
                renderValue={(selected) => getSelectedNames(selected)}
              >
                {assistantCoaches.map((coach) => (
                  <MenuItem key={coach.id} value={coach.id}>
                    <Checkbox disabled={isPodUpdating} checked={selectedACs.includes(coach.id)} />
                    <ListItemText primary={coach.firstName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContentContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleUpdateACModalClose}>{texts.ok}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeletePodModalClose}
        aria-labelledby="delete-pod"
        aria-describedby="delete-pod-confirmation"
      >
        <DialogTitle id="delete-pod">
          {texts.deletePod}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {texts.deletePodConfirmation}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeletePodModalClose}>{texts.cancel}</Button>
          <LoadingButton
            loading={isPodDeleting}
            disabled={isPodDeleting}
            variant="contained"
            onClick={handlePodDelete}
            autoFocus
          >
            {texts.delete}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>

  );
};

CoachPodTable.propTypes = {
  selectedCoach: PropTypes.object.isRequired,
};

export default compose(
  observer,
)(CoachPodTable);
